import { useEffect, useState, type ReactNode } from "react";
import { router } from "@inertiajs/react";
import { PortalSidebar } from "./PortalSidebar";
import { PortalTopBar } from "./PortalTopBar";
import { FlashToast } from "@/components/FlashToast";

export function PortalShell({ children }: { children: ReactNode }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close the mobile drawer whenever an Inertia navigation completes.
  useEffect(() => router.on("navigate", () => setMobileOpen(false)), []);

  // Apply Stabiliti brand at the document root while the portal is mounted
  // so Radix-portaled surfaces (Menu, Tooltip, Dialog) inherit brand tokens.
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.getAttribute("data-brand");
    root.setAttribute("data-brand", "stabiliti");
    return () => {
      if (previous == null) root.removeAttribute("data-brand");
      else root.setAttribute("data-brand", previous);
    };
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  return (
    <div
      data-brand="stabiliti"
      className="flex h-screen w-full overflow-hidden bg-canvas text-fg"
    >
      <div className="hidden md:flex">
        <PortalSidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed((c) => !c)}
        />
      </div>

      {mobileOpen && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
            className="fixed inset-0 z-40 bg-overlay backdrop-blur-[2px] md:hidden"
          />
          <div className="fixed inset-y-0 left-0 z-50 md:hidden shadow-[var(--shadow-4)]">
            <PortalSidebar
              collapsed={false}
              onToggle={() => {}}
              mobile
              onClose={() => setMobileOpen(false)}
            />
          </div>
        </>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <PortalTopBar
          sidebarCollapsed={sidebarCollapsed}
          onExpandSidebar={() => setSidebarCollapsed(false)}
          onOpenMobileMenu={() => setMobileOpen(true)}
        />
        <main className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {children}
        </main>
      </div>
      <FlashToast />
    </div>
  );
}
